import {Glyph} from '@visx/glyph';
import {PatternLines} from '@visx/pattern';
import {
  AnimatedAreaSeries,
  AnimatedBarSeries,
  AnimatedGlyphSeries,
  AnimatedLineSeries,
  Tooltip,
  XYChart,
  buildChartTheme,
  TooltipContext,
} from '@visx/xychart';
import {formattedPropertyModel, utils} from '@wallaby-ui/core';
import {debounce, isEqual, isNil, minBy, maxBy, forEach} from 'lodash';
import PropTypes from 'prop-types';
import * as React from 'react';
import {FormattedDate, injectIntl} from 'react-intl';
import ResizeObserver from 'resize-observer-polyfill';

import SparklinesChartHorizontalLine, {
  DEFAULT_HORIZONTAL_REFERENCE_LINE_COLOR,
  DEFAULT_HORIZONTAL_REFERENCE_LINE_REFERENCE,
  DEFAULT_HV_LINE_STROKE_DASHARRAY,
} from './SparklinesChartHorizontalLine';

const SPARKLINE_CHART_TYPE_LINE = 'line';
const SPARKLINE_CHART_TYPE_BAR = 'bar';

const FONT_FAMILY_NORMAL =
  'SourceSansPro-Regular, Lato-Medium, Helvetica Neue, Helvetica, Arial, sans-serif';
const FONT_FAMILY_BOLD =
  'SourceSansPro-Bold, Lato-Black, Helvetica Neue, Helvetica, Arial, sans-serif';
const FONT_SIZE_SMALL = '12px';
const FONT_SIZE_NORMAL = '14px';
const FONT_COLOR = '#4f5e6e';

const BAR_SERIES_DATA_KEY = 'barSeries';
const LINE_SERIES_DATA_KEY = 'lineSeries';
const GLYPH_SERIES_DATA_KEY = 'glyphSeries';
const AREA_PATTERN_DATA_KEY = 'patternLines';
const AREA_PATTERN_ID = 'area_pattern';
const HORIZONTAL_REFERENCE_LINE_DATA_KEY = 'horizontalReferenceLine';

// Constants for default settings of SparklineChart
const DEFAULT_STROKE_WIDTH = 1;

const DEFAULT_BAR_COLOR = '#56c0e4';
const DEFAULT_BAR_HIGHLIGHT_COLOR = '#175fe4';
const DEFAULT_BAR_LINE_COLOR = '#175fe4';
const DEFAULT_BAR_INNER_PADDING = 0.2;
const DEFAULT_BAR_OUTTER_PADDING = 0;

const DEFAULT_LINE_COLOR = '#56c0e4';

const DEFAULT_PATTERN_LINE_ORIENTATION = ['diagonal'];
const DEFAULT_PATTERN_LINE_SIZE = 4;

const DEFAULT_POINT_COLOR = '#175fe4';
const DEFAULT_POINT_SIZE_FIRST_LAST = 5;

const DEFAULT_SPARKLINE_ARIA_LABEL = 'Sparkline';
const DEFAULT_SPARKLINE_HEIGHT = 30;
// Margin top and bottom for display points
const DEFAULT_SPARKLINE_MARGIN = {
  top: 7,
  right: 44,
  bottom: 5,
  left: 44,
};
const DEFAULT_SPARKLINE_MAX_VALUE = undefined;
const DEFAULT_SPARKLINE_MIN_VALUE = undefined;
const DEFAULT_SPARKLINE_WIDTH = 240;

const DEFAULT_POINT_FIRST_LAST_OFFSET = 20;

const accessors = {
  xAccessor: (d) => d.x,
  yAccessor: (d) => d.y,
};

class SparklinesChart extends React.PureComponent {
  static getDerivedStateFromProps(props, state) {
    let resultState = null;
    if (!isEqual(props.chartType, state.prevChartType)) {
      resultState = {
        chartTheme: buildChartTheme({
          // categorical colors, mapped to series via `dataKey`s
          colors:
            props.chartType === SPARKLINE_CHART_TYPE_LINE
              ? [DEFAULT_LINE_COLOR, DEFAULT_POINT_COLOR]
              : [DEFAULT_BAR_COLOR, DEFAULT_BAR_LINE_COLOR],

          // labels
          svgLabelBig: {
            fontFamily: FONT_FAMILY_BOLD,
            fontSize: FONT_SIZE_NORMAL,
            fill: FONT_COLOR,
          },
          svgLabelSmall: {
            fontFamily: FONT_FAMILY_NORMAL,
            fontSize: FONT_SIZE_SMALL,
            fill: FONT_COLOR,
          },
          htmlLabel: {
            fontFamily: FONT_FAMILY_NORMAL,
            fontSize: FONT_SIZE_NORMAL,
            color: FONT_COLOR,
          },
        }),
        prevChartType: props.chartType,
      };
    }
    return resultState;
  }

  constructor() {
    super();
    this.debouncedResize = debounce(this.handleResize, 500).bind(this);
    this.renderLabel = this.renderLabel.bind(this);
    this.renderTooltip = this.renderTooltip.bind(this);

    this.state = {
      sparklinesWidth: DEFAULT_SPARKLINE_WIDTH,
    };

    this.cancelablePromises = [];
    this.parentDivRef = React.createRef();
    this.ro = null;
  }

  componentDidMount() {
    if (ResizeObserver) {
      this.ro = new ResizeObserver(this.debouncedResize);
      this.ro.observe(this.parentDivRef.current);
    }
  }

  componentWillUnmount() {
    if (this.ro) {
      this.ro.disconnect();
    }

    forEach(this.cancelablePromises, (cancelablePromise) => {
      cancelablePromise.cancel();
    });
  }

  static getExtreme(data, extremeFunction) {
    let extreme = null;
    if (extremeFunction && data) {
      // we have to use isNil, because if min is 0,
      // the condition return if (item.y) return false !!!
      const extremeItem = extremeFunction(data, (item) =>
        item && !isNil(item.y) ? Number(item.y) : null
      );
      if (extremeItem && !isNil(extremeItem.y)) {
        extreme = Number(extremeItem.y);
      }
    }
    return extreme;
  }

  handleResize() {
    window.requestAnimationFrame(() => {
      const {sparklinesChartProps} = this.props;

      const cancelablePromise = utils.PromiseUtils.makeCancelable(
        Promise.resolve()
      );
      this.cancelablePromises.push(cancelablePromise);
      cancelablePromise.promise
        .then(() => {
          const el = this.parentDivRef.current;
          if (el) {
            this.setState({
              sparklinesWidth:
                el.offsetWidth || sparklinesChartProps.sparklineDefault.width,
            });
          }
        })
        .catch(utils.PromiseUtils.handleCanceledPromise);
    });
  }

  renderLabel({size, datum}) {
    const {intl, data} = this.props;
    const isFirst =
      data && data.length > 0
        ? data.findIndex((item) => isEqual(item, datum)) === 0
        : false;
    const propDefinition = new formattedPropertyModel.FormattedProperty({
      type: formattedPropertyModel.FORMATTED_PROPERTY_TYPE_DECIMAL,
    });
    const y = accessors.yAccessor(datum);
    const text = y
      ? formattedPropertyModel.FormattedProperty.formatValue(
          propDefinition,
          y,
          intl
        )
      : '0';
    return (
      <Glyph>
        <g>
          <circle
            r={size}
            fill={DEFAULT_POINT_COLOR}
            stroke={'#fff'}
            strokeWidth={'2'}
            fillOpacity={'1'}
          />
          <text
            dx={(isFirst ? -1 : 1) * DEFAULT_POINT_FIRST_LAST_OFFSET}
            dy={0}
            dominantBaseline={'middle'}
            textAnchor={isFirst ? 'end' : 'start'}
            pointerEvents={'none'}
            fontFamily={FONT_FAMILY_NORMAL}
            fontSize={FONT_SIZE_NORMAL}
            fill={FONT_COLOR}
          >
            {text}
          </text>
        </g>
      </Glyph>
    );
  }

  renderTooltip({tooltipData}) {
    const {intl} = this.props;
    const x = accessors.xAccessor(tooltipData.nearestDatum.datum);
    const y = accessors.yAccessor(tooltipData.nearestDatum.datum);
    const propDefinition = new formattedPropertyModel.FormattedProperty({
      type: formattedPropertyModel.FORMATTED_PROPERTY_TYPE_DECIMAL,
    });
    return (
      <div>
        {x && !Number.isNaN(Date.parse(x)) ? <FormattedDate value={x} /> : null}
        <div>
          {y
            ? formattedPropertyModel.FormattedProperty.formatValue(
                propDefinition,
                y,
                intl
              )
            : '0'}
        </div>
      </div>
    );
  }

  render() {
    const {sparklinesWidth, chartTheme} = this.state;
    const {chartType, data, sparklinesChartProps, minMaxFromData} = this.props;
    const min = minMaxFromData
      ? SparklinesChart.getExtreme(data, minBy)
      : sparklinesChartProps.sparklineDefault.min;
    const max = minMaxFromData
      ? SparklinesChart.getExtreme(data, maxBy)
      : sparklinesChartProps.sparklineDefault.max;
    // we have to set artificial max, because otherwise the bar has non-zero height
    const artificialMax = min === 0 && max === 0 ? 1 : max;

    // create glyph series data as the first and the last item from data
    const glyphData = [data[0], data[data.length - 1]];

    // scales
    const xScaleConfig = {
      paddingInner: DEFAULT_BAR_INNER_PADDING,
      paddingOuter: DEFAULT_BAR_OUTTER_PADDING,
    };
    const yScaleConfig = {
      domain: [min, artificialMax > max ? artificialMax : max],
      range: [
        sparklinesChartProps.sparklineDefault.height -
          sparklinesChartProps.sparklineDefault.margin.bottom,
        sparklinesChartProps.sparklineDefault.margin.top,
      ],
      round: false,
      zero: false,
    };

    const horizontalReferenceLineDatum = {
      // add reference to the last data point, it is necessary for determinig annotaion label position
      x:
        data && data[data.length - 1]
          ? accessors.xAccessor(data[data.length - 1])
          : null,
      // value for horizontalReferenceLine
      y:
        chartType === SPARKLINE_CHART_TYPE_LINE
          ? sparklinesChartProps.horizontalReferenceLine.reference
          : max,
    };

    return (
      <div ref={this.parentDivRef}>
        {this.parentDivRef.current &&
        this.parentDivRef.current.offsetWidth > 0 ? (
          <XYChart
            theme={chartTheme}
            accessibilityLabel={sparklinesChartProps.sparklineDefault.ariaLabel}
            width={sparklinesWidth}
            height={sparklinesChartProps.sparklineDefault.height}
            xScale={{type: 'band', ...xScaleConfig}}
            yScale={{type: 'linear', ...yScaleConfig}}
            margin={sparklinesChartProps.sparklineDefault.margin}
          >
            {chartType === SPARKLINE_CHART_TYPE_LINE && (
              <>
                <PatternLines
                  id={AREA_PATTERN_ID}
                  key={AREA_PATTERN_DATA_KEY}
                  height={DEFAULT_PATTERN_LINE_SIZE}
                  width={DEFAULT_PATTERN_LINE_SIZE}
                  stroke={DEFAULT_LINE_COLOR}
                  strokeWidth={DEFAULT_STROKE_WIDTH}
                  orientation={DEFAULT_PATTERN_LINE_ORIENTATION}
                />
                <AnimatedAreaSeries
                  dataKey={LINE_SERIES_DATA_KEY}
                  data={data}
                  {...accessors}
                  stroke={sparklinesChartProps.lineSeriesStrokeColor}
                  fill={`url(#${AREA_PATTERN_ID})`}
                />
                <AnimatedGlyphSeries
                  dataKey={GLYPH_SERIES_DATA_KEY}
                  data={glyphData}
                  {...accessors}
                  size={DEFAULT_POINT_SIZE_FIRST_LAST}
                  fill={DEFAULT_POINT_COLOR}
                  renderGlyph={this.renderLabel}
                />
                <SparklinesChartHorizontalLine
                  dataKey={HORIZONTAL_REFERENCE_LINE_DATA_KEY}
                  datum={horizontalReferenceLineDatum}
                  {...accessors}
                  stroke={
                    sparklinesChartProps.horizontalReferenceLine.strokeColor
                  }
                  strokeWidth={DEFAULT_STROKE_WIDTH}
                  strokeDasharray={DEFAULT_HV_LINE_STROKE_DASHARRAY}
                  hideLabel
                />
              </>
            )}
            {chartType === SPARKLINE_CHART_TYPE_BAR && (
              <TooltipContext.Consumer>
                {(tooltipProps) => {
                  horizontalReferenceLineDatum.y = tooltipProps.tooltipOpen
                    ? accessors.yAccessor(
                        tooltipProps.tooltipData.datumByKey[BAR_SERIES_DATA_KEY]
                          .datum
                      )
                    : max;
                  return (
                    <>
                      <AnimatedBarSeries
                        dataKey={BAR_SERIES_DATA_KEY}
                        data={data}
                        {...accessors}
                        colorAccessor={(d, datumIdx) => {
                          const {tooltipOpen, tooltipData} = tooltipProps;
                          let resultColor = null;
                          if (tooltipOpen) {
                            resultColor =
                              tooltipData.datumByKey[BAR_SERIES_DATA_KEY] &&
                              tooltipData.datumByKey[BAR_SERIES_DATA_KEY]
                                .index === datumIdx
                                ? DEFAULT_BAR_HIGHLIGHT_COLOR
                                : null;
                          }
                          return resultColor;
                        }}
                        fillOpacity={0.9}
                        strokeWidth={0}
                      />
                      <AnimatedLineSeries
                        dataKey={LINE_SERIES_DATA_KEY}
                        data={data}
                        {...accessors}
                        stroke={DEFAULT_BAR_LINE_COLOR}
                        strokeWidth={DEFAULT_STROKE_WIDTH}
                      />
                      <SparklinesChartHorizontalLine
                        dataKey={HORIZONTAL_REFERENCE_LINE_DATA_KEY}
                        datum={horizontalReferenceLineDatum}
                        {...accessors}
                        stroke={
                          sparklinesChartProps.horizontalReferenceLine
                            .strokeColor
                        }
                        strokeWidth={DEFAULT_STROKE_WIDTH}
                        strokeDasharray={DEFAULT_HV_LINE_STROKE_DASHARRAY}
                        label={String(horizontalReferenceLineDatum.y)}
                        labelX={
                          sparklinesWidth -
                          sparklinesChartProps.sparklineDefault.margin.right
                        }
                        labelY={25}
                      />
                    </>
                  );
                }}
              </TooltipContext.Consumer>
            )}
            <Tooltip
              snapTooltipToDatumX
              snapTooltipToDatumY
              showVerticalCrosshair
              showSeriesGlyphs
              renderTooltip={this.renderTooltip}
            />
          </XYChart>
        ) : null}
      </div>
    );
  }
}

SparklinesChart.displayName = 'SparklinesChart';

SparklinesChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.any),
  chartType: PropTypes.oneOf([
    SPARKLINE_CHART_TYPE_LINE,
    SPARKLINE_CHART_TYPE_BAR,
  ]),
  sparklinesChartProps: PropTypes.shape({
    sparklineDefault: PropTypes.shape({
      ariaLabel: PropTypes.string,
      min: PropTypes.number,
      max: PropTypes.number,
      width: PropTypes.number,
      height: PropTypes.number,
      margin: PropTypes.objectOf(PropTypes.any),
    }),
    horizontalReferenceLine: PropTypes.shape({
      strokeColor: PropTypes.string,
      reference: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    lineSeriesStrokeColor: PropTypes.string,
    pointSeriesFillColor: PropTypes.string,
    barSeriesFillColor: PropTypes.string,
  }),
  minMaxFromData: PropTypes.bool,
};

SparklinesChart.defaultProps = {
  data: [],
  chartType: SPARKLINE_CHART_TYPE_LINE,
  sparklinesChartProps: {
    // default properties for Sparkline component,
    // configurable(ariaLabel, min, max, width, height, margin)
    sparklineDefault: {
      ariaLabel: DEFAULT_SPARKLINE_ARIA_LABEL,
      min: DEFAULT_SPARKLINE_MIN_VALUE,
      max: DEFAULT_SPARKLINE_MAX_VALUE,
      width: DEFAULT_SPARKLINE_WIDTH,
      height: DEFAULT_SPARKLINE_HEIGHT,
      margin: DEFAULT_SPARKLINE_MARGIN,
    },
    // default properties for HorizontalReferenceLine component,
    // configurable(strokeColor, reference)
    horizontalReferenceLine: {
      strokeColor: DEFAULT_HORIZONTAL_REFERENCE_LINE_COLOR,
      reference: DEFAULT_HORIZONTAL_REFERENCE_LINE_REFERENCE,
    },
    // default property for LineSeries component - stroke color
    lineSeriesStrokeColor: DEFAULT_LINE_COLOR,
    // default property for LineSeries component - fill color
    pointSeriesFillColor: DEFAULT_POINT_COLOR,
    barSeriesFillColor: DEFAULT_BAR_COLOR,
  },
  // determine if min and max are set to data extremes
  minMaxFromData: false,
};

export default injectIntl(SparklinesChart);
